import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/pos-api";
import { rupiah, rupiahShort } from "@/lib/format";
import { toast } from "sonner";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  CalendarIcon, FileSpreadsheet, FileText, TrendingUp, Coins, Receipt, Package,
  ChevronDown, AlertTriangle, Boxes, TrendingDown, Search, ArrowLeftRight,
} from "lucide-react";

const fmt = (d) => format(d, "yyyy-MM-dd");
const label = (d) => format(d, "d MMM yyyy", { locale: idLocale });

export default function Reports() {
  const [range, setRange] = useState({ from: new Date(), to: new Date() });
  const [calOpen, setCalOpen] = useState(false);
  const [sales, setSales] = useState(null);
  const [products, setProducts] = useState(null);
  const [closing, setClosing] = useState(null);
  const [tab, setTab] = useState("penjualan");
  const [expTx, setExpTx] = useState(null);
  const [movements, setMovements] = useState(null);
  const [mvType, setMvType] = useState("all");
  const [mvQ, setMvQ] = useState("");

  const start = range?.from ? fmt(range.from) : fmt(new Date());
  const end = range?.to ? fmt(range.to) : start;

  const load = async () => {
    try {
      const params = { start, end };
      const [s, p, c] = await Promise.all([
        api.get("/reports/sales", { params }),
        api.get("/reports/products", { params }),
        api.get("/reports/closing", { params }),
      ]);
      setSales(s.data); setProducts(p.data); setClosing(c.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  useEffect(() => {
    api.get("/reports/stock-movements", { params: { start, end, type: mvType, q: mvQ } })
      .then((r) => setMovements(r.data))
      .catch((e) => toast.error(apiError(e)));
    /* eslint-disable-next-line */
  }, [start, end, mvType, mvQ]);

  const download = async (type, format2, extra = {}) => {
    try {
      const res = await api.get("/reports/export", { params: { type, format: format2, start, end, ...extra }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${type}.${format2 === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const preset = (days, mode) => {
    const now = new Date();
    if (mode === "today") setRange({ from: now, to: now });
    else if (mode === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); setRange({ from: y, to: y }); }
    else if (mode === "month") setRange({ from: new Date(now.getFullYear(), now.getMonth(), 1), to: now });
    else { const f = new Date(now); f.setDate(f.getDate() - (days - 1)); setRange({ from: f, to: now }); }
    setCalOpen(false);
  };

  const rangeText = range?.from
    ? (range.to && fmt(range.to) !== fmt(range.from) ? `${label(range.from)} — ${label(range.to)}` : label(range.from))
    : "Pilih tanggal";

  return (
    <>
      <PageHeader title="Laporan" subtitle="Penjualan, keuntungan, produk & closing shift">
        <Popover open={calOpen} onOpenChange={setCalOpen}>
          <PopoverTrigger asChild>
            <Button data-testid="report-date-btn" variant="outline" className="border-slate-300 bg-[#F7F9FB] hover:bg-[#F7F9FB] gap-2 h-10">
              <CalendarIcon className="h-4 w-4 text-emerald-600" />
              <span className="text-sm">{rangeText}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto p-0 bg-[#F7F9FB] border-slate-300 text-slate-800" data-testid="report-calendar">
            <div className="flex flex-wrap gap-1.5 p-3 border-b border-slate-200">
              <Chip onClick={() => preset(0, "today")}>Hari ini</Chip>
              <Chip onClick={() => preset(0, "yesterday")}>Kemarin</Chip>
              <Chip onClick={() => preset(7)}>7 Hari</Chip>
              <Chip onClick={() => preset(30)}>30 Hari</Chip>
              <Chip onClick={() => preset(0, "month")}>Bulan Ini</Chip>
            </div>
            <Calendar
              mode="range"
              selected={range}
              onSelect={(r) => setRange(r || { from: new Date(), to: new Date() })}
              numberOfMonths={2}
              locale={idLocale}
            />
          </PopoverContent>
        </Popover>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5" data-testid="reports-page">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#F7F9FB] border border-slate-300">
            <TabsTrigger value="penjualan" data-testid="tab-penjualan">Penjualan</TabsTrigger>
            <TabsTrigger value="transaksi" data-testid="tab-transaksi">Per Transaksi</TabsTrigger>
            <TabsTrigger value="produk" data-testid="tab-produk">Produk & Stok</TabsTrigger>
            <TabsTrigger value="closing" data-testid="tab-closing">Closing Shift</TabsTrigger>
            <TabsTrigger value="mutasi" data-testid="tab-mutasi">Mutasi Stok</TabsTrigger>
          </TabsList>

          {/* PENJUALAN */}
          <TabsContent value="penjualan" className="mt-5 space-y-5">
            <ExportBar onExcel={() => download("sales", "xlsx")} onPdf={() => download("sales", "pdf")} />
            {sales && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Pendapatan" value={rupiah(sales.summary.revenue)} icon={TrendingUp} color="text-emerald-600" bg="bg-emerald-500/10" testid="stat-revenue" />
                  <Stat label="Keuntungan" value={rupiah(sales.summary.profit)} icon={Coins} color="text-amber-600" bg="bg-amber-500/10" sub={`Margin ${sales.summary.margin.toFixed(1)}%`} testid="stat-profit" />
                  <Stat label="Modal (HPP)" value={rupiah(sales.summary.cogs)} icon={Package} color="text-blue-400" bg="bg-blue-500/10" />
                  <Stat label="Transaksi" value={`${sales.summary.tx_count} · ${sales.summary.items_sold} item`} icon={Receipt} color="text-indigo-400" bg="bg-indigo-500/10" />
                </div>

                <Panel title="Tren Pendapatan & Untung">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={sales.by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                      <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                      <YAxis stroke="#64748B" fontSize={12} tickFormatter={rupiahShort} width={60} />
                      <Tooltip contentStyle={{ background: "#F7F9FB", border: "1px solid #cbd5e1", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
                      <Line type="monotone" dataKey="revenue" name="Pendapatan" stroke="#10B981" strokeWidth={3} dot={false} />
                      <Line type="monotone" dataKey="profit" name="Untung" stroke="#F59E0B" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel title="Item Penjualan per Hari (dengan Keuntungan)">
                  <SimpleTable
                    testid="items-table"
                    head={["Produk", "Satuan", "Qty", "Pendapatan", "Modal", "Untung"]}
                    align={["l", "l", "r", "r", "r", "r"]}
                    rows={sales.items.map((i) => [i.name, i.unit, i.qty, rupiah(i.revenue), rupiah(i.cogs), <span className="text-amber-600 font-semibold">{rupiah(i.profit)}</span>])}
                    empty="Belum ada penjualan pada rentang ini"
                  />
                </Panel>
              </>
            )}
          </TabsContent>

          {/* PER TRANSAKSI */}
          <TabsContent value="transaksi" className="mt-5 space-y-3">
            {sales && (sales.transactions.length === 0 ? (
              <Empty text="Belum ada transaksi pada rentang ini" />
            ) : (
              sales.transactions.map((t) => {
                const open = expTx === t.id;
                return (
                  <div key={t.id} data-testid={`tx-${t.id}`} className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                    <button onClick={() => setExpTx(open ? null : t.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F7F9FB]">
                      <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm font-semibold">{t.invoice_no}</div>
                        <div className="text-[11px] text-slate-500">{new Date(t.created_at).toLocaleString("id-ID")} · {t.cashier} · {t.item_count} item</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-emerald-600">{rupiah(t.total)}</div>
                        <div className="text-[11px] text-amber-600">Untung {rupiah(t.profit)}</div>
                      </div>
                    </button>
                    {open && (
                      <div className="border-t border-slate-200">
                        <SimpleTable
                          head={["Produk", "Satuan", "Qty", "Harga", "Modal", "Untung"]}
                          align={["l", "l", "r", "r", "r", "r"]}
                          rows={t.items.map((i) => [i.name, i.unit, i.qty, rupiah(i.price), rupiah(i.cost), <span className="text-amber-600 font-semibold">{rupiah(i.profit)}</span>])}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            ))}
          </TabsContent>

          {/* PRODUK & STOK */}
          <TabsContent value="produk" className="mt-5 space-y-5">
            <ExportBar onExcel={() => download("products", "xlsx")} onPdf={() => download("products", "pdf")} />
            {products && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <Stat label="Total Produk" value={products.totals.product_count} icon={Package} color="text-indigo-400" bg="bg-indigo-500/10" />
                  <Stat label="Total Sisa Stok (pcs)" value={products.totals.total_stock} icon={Boxes} color="text-emerald-600" bg="bg-emerald-500/10" />
                  <Stat label="Nilai Stok (modal)" value={rupiah(products.totals.stock_value)} icon={Coins} color="text-amber-600" bg="bg-amber-500/10" />
                </div>

                {(() => {
                  const minus = products.products.filter((p) => p.stock < 0);
                  return minus.length > 0 ? (
                    <Panel title="⚠ Stok Minus — Terjual Melebihi Stok (perlu segera dibeli)">
                      <SimpleTable
                        testid="stock-minus-table"
                        head={["Produk", "Kategori", "Stok (minus)", "Terjual"]}
                        align={["l", "l", "r", "r"]}
                        rows={minus.map((p) => [
                          p.name, p.category,
                          <span className="text-red-600 font-bold inline-flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" />{p.stock}</span>,
                          p.sold_qty,
                        ])}
                      />
                    </Panel>
                  ) : null;
                })()}
                <Panel title="Sisa Stok per Kategori">
                  <SimpleTable
                    testid="stock-category-table"
                    head={["Kategori", "Jml Produk", "Total Stok", "Nilai Stok"]}
                    align={["l", "r", "r", "r"]}
                    rows={products.by_category.map((c) => [c.category, c.product_count, c.total_stock, rupiah(c.stock_value)])}
                    empty="Belum ada produk"
                  />
                </Panel>

                <Panel title="Laporan Produk & Sisa Stok">
                  <SimpleTable
                    testid="product-report-table"
                    head={["Produk", "Kategori", "Sisa Stok", "Min", "Modal", "Harga", "Nilai", "Terjual"]}
                    align={["l", "l", "r", "r", "r", "r", "r", "r"]}
                    rows={products.products.map((p) => [
                      p.name, p.category,
                      <span className={p.low ? "text-amber-600 font-semibold inline-flex items-center gap-1" : ""}>{p.low && <AlertTriangle className="h-3 w-3" />}{p.stock}</span>,
                      p.min_stock, rupiah(p.cost), rupiah(p.price), rupiah(p.stock_value), p.sold_qty,
                    ])}
                    empty="Belum ada produk"
                  />
                </Panel>
              </>
            )}
          </TabsContent>

          {/* CLOSING SHIFT */}
          <TabsContent value="closing" className="mt-5 space-y-5">
            <ExportBar onExcel={() => download("closing", "xlsx")} onPdf={() => download("closing", "pdf")} />
            {closing && (
              <Panel title={`Closing Shift per Kasir · ${start === end ? label(range.from) : rangeText}`}>
                <SimpleTable
                  testid="closing-table"
                  head={["Kasir", "Transaksi", "Item", "Penjualan", "Untung", "Tunai", "Transfer", "QRIS", "Kembalian", "Piutang", "Pelunasan"]}
                  align={["l", "r", "r", "r", "r", "r", "r", "r", "r", "r", "r"]}
                  rows={closing.rows.map((r) => [
                    r.cashier, r.tx_count, r.items_sold, rupiah(r.revenue),
                    <span className="text-amber-600 font-semibold">{rupiah(r.profit)}</span>,
                    rupiah(r.tunai), rupiah(r.transfer), rupiah(r.qris), rupiah(r.change),
                    <span className="text-red-600">{rupiah(r.piutang || 0)}</span>,
                    <span className="text-emerald-600 font-semibold">{rupiah(r.pelunasan || 0)}</span>,
                  ])}
                  footer={closing.rows.length ? [
                    "TOTAL", closing.totals.tx_count, closing.totals.items_sold, rupiah(closing.totals.revenue),
                    rupiah(closing.totals.profit), rupiah(closing.totals.tunai), rupiah(closing.totals.transfer),
                    rupiah(closing.totals.qris), rupiah(closing.totals.change),
                    rupiah(closing.totals.piutang || 0), rupiah(closing.totals.pelunasan || 0),
                  ] : null}
                  empty="Belum ada transaksi pada rentang ini"
                />
              </Panel>
            )}
          </TabsContent>

          {/* MUTASI STOK */}
          <TabsContent value="mutasi" className="mt-5 space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Select value={mvType} onValueChange={setMvType}>
                <SelectTrigger data-testid="mutasi-type-filter" className="w-[160px] h-9 border-slate-300 bg-[#F7F9FB]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="Pembelian">Pembelian</SelectItem>
                  <SelectItem value="Penjualan">Penjualan</SelectItem>
                  <SelectItem value="Opname">Opname</SelectItem>
                  <SelectItem value="Transfer">Transfer</SelectItem>
                  <SelectItem value="Refund">Refund</SelectItem>
                  <SelectItem value="Penyesuaian">Penyesuaian</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  data-testid="mutasi-search"
                  value={mvQ}
                  onChange={(e) => setMvQ(e.target.value)}
                  placeholder="Cari produk…"
                  className="pl-9 h-9 border-slate-300 bg-[#F7F9FB]"
                />
              </div>
              <div className="ml-auto">
                <ExportBar
                  onExcel={() => download("stock-movements", "xlsx", { mv_type: mvType, q: mvQ })}
                  onPdf={() => download("stock-movements", "pdf", { mv_type: mvType, q: mvQ })}
                />
              </div>
            </div>

            {movements && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat label="Total Masuk (+)" value={`+${movements.totals.in}`} icon={TrendingUp} color="text-emerald-600" bg="bg-emerald-500/10" testid="stat-mv-in" />
                  <Stat label="Total Keluar (−)" value={`−${movements.totals.out}`} icon={TrendingDown} color="text-red-600" bg="bg-red-500/10" testid="stat-mv-out" />
                  <Stat label="Mutasi Bersih" value={`${movements.totals.net >= 0 ? "+" : ""}${movements.totals.net}`} icon={ArrowLeftRight} color="text-amber-600" bg="bg-amber-500/10" testid="stat-mv-net" />
                  <Stat label="Jumlah Mutasi" value={movements.totals.count} icon={Boxes} color="text-indigo-400" bg="bg-indigo-500/10" testid="stat-mv-count" />
                </div>

                <Panel title="Ringkasan per Jenis Mutasi">
                  <SimpleTable
                    testid="mutasi-summary-table"
                    head={["Jenis", "Masuk (+)", "Keluar (−)", "Bersih", "Jml"]}
                    align={["l", "r", "r", "r", "r"]}
                    rows={movements.summary.map((g) => [
                      g.type,
                      <span className="text-emerald-600 font-semibold">+{g.in}</span>,
                      <span className="text-red-600 font-semibold">−{g.out}</span>,
                      <span className={g.net >= 0 ? "text-emerald-600" : "text-red-600"}>{g.net >= 0 ? "+" : ""}{g.net}</span>,
                      g.count,
                    ])}
                    footer={movements.summary.length ? [
                      "TOTAL",
                      `+${movements.totals.in}`,
                      `−${movements.totals.out}`,
                      `${movements.totals.net >= 0 ? "+" : ""}${movements.totals.net}`,
                      movements.totals.count,
                    ] : null}
                    empty="Belum ada mutasi pada rentang ini"
                  />
                </Panel>

                <Panel title={`Detail Mutasi Stok · ${start === end ? label(range.from) : rangeText}`}>
                  <SimpleTable
                    testid="mutasi-detail-table"
                    head={["Waktu", "Jenis", "Produk", "Perubahan", "Catatan", "Oleh"]}
                    align={["l", "l", "l", "r", "l", "l"]}
                    rows={movements.movements.map((m) => [
                      m.at_label,
                      <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-slate-200/60 text-slate-700">{m.type}</span>,
                      m.product_name,
                      <span className={m.delta >= 0 ? "text-emerald-600 font-bold" : "text-red-600 font-bold"}>{m.delta >= 0 ? `+${m.delta}` : m.delta}</span>,
                      <span className="text-slate-500 text-xs">{m.note || "-"}</span>,
                      <span className="text-slate-500 text-xs">{m.by || "-"}</span>,
                    ])}
                    empty="Belum ada mutasi pada rentang ini"
                  />
                </Panel>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function Chip({ children, onClick }) {
  return (
    <button onClick={onClick} className="px-2.5 h-8 rounded-lg text-xs font-medium bg-[#F7F9FB] border border-slate-300 hover:border-emerald-500 text-slate-700 no-select">
      {children}
    </button>
  );
}

function ExportBar({ onExcel, onPdf }) {
  return (
    <div className="flex justify-end gap-2">
      <Button data-testid="export-excel-btn" onClick={onExcel} variant="outline" className="border-emerald-600/50 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 gap-2 h-9">
        <FileSpreadsheet className="h-4 w-4" /> Excel
      </Button>
      <Button data-testid="export-pdf-btn" onClick={onPdf} variant="outline" className="border-red-600/50 bg-red-500/10 hover:bg-red-500/20 text-red-500 gap-2 h-9">
        <FileText className="h-4 w-4" /> PDF
      </Button>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, bg, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</span>
        <div className={`h-8 w-8 rounded-lg ${bg} flex items-center justify-center`}><Icon className={`h-4 w-4 ${color}`} /></div>
      </div>
      <div className="font-mono font-extrabold text-xl mt-2">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <h3 className="font-heading font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500 text-sm">{text}</div>;
}

function SimpleTable({ head, rows, align = [], footer, empty, testid }) {
  const cls = (i) => (align[i] === "r" ? "text-right" : "text-left");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="text-slate-500 text-[11px] uppercase tracking-wide border-b border-slate-200">
          <tr>{head.map((h, i) => <th key={i} className={`py-2 px-2 ${cls(i)}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="py-8 text-center text-slate-500">{empty}</td></tr>
          ) : rows.map((r, ri) => (
            <tr key={ri} className="border-b border-slate-200 hover:bg-slate-50">
              {r.map((c, ci) => <td key={ci} className={`py-2 px-2 ${cls(ci)} ${ci === 0 ? "font-medium" : "font-mono"}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t-2 border-slate-300 font-bold bg-slate-50">
              {footer.map((c, ci) => <td key={ci} className={`py-2 px-2 ${cls(ci)} ${ci === 0 ? "" : "font-mono"}`}>{c}</td>)}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
